import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { cleAppareil } from '@stockflow/offline-core';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';

/** Ce que le plan de l'entreprise donne au POS mobile : vendre, ne faire que du RH, ou rien. */
export type ModePos = 'SALES' | 'HR' | 'NONE';

const MODES: readonly ModePos[] = ['SALES', 'HR', 'NONE'];

/** Prefixe des clefs `localStorage` — voir la ligne `mode-pos` de `PURGEURS_VENTE`. */
export const PREFIXE_MODE_POS = 'stockflow.mode-pos.';

const DELAI_MS = 4000;

/** Reponse de `GET /plans/mode-pos/:companyId` (partners-service, `modePosEntreprise`). */
export interface ReponseModePos {
    hasPlan: boolean;
    posMobileMode: string;
    canSell: boolean;
}

/**
 * Mode a appliquer pour une reponse du serveur.
 *
 * Sans plan (abonnement termine, entreprise sans plan), on GARDE le menu actuel :
 * c'est la decision D6 — le serveur reste l'autorite et refuse les ventes que le plan
 * n'autorise pas. Une valeur inconnue, elle, ferme : meme regle que le serveur.
 */
export function modeEffectif(reponse: ReponseModePos): ModePos {
    if (!reponse.hasPlan) return 'SALES';
    return (MODES as readonly string[]).includes(reponse.posMobileMode) ? (reponse.posMobileMode as ModePos) : 'NONE';
}

/**
 * LE MODE DU POS MOBILE DE L'ENTREPRISE COURANTE.
 *
 * Le plan decide du menu de `vente/` : SALES garde le menu actuel, HR montre le menu
 * « sans vente », NONE ne laisse que l'ecran « Votre plan n'inclut pas l'application
 * mobile ».
 *
 * Hors ligne : la derniere valeur lue est gardee CHIFFREE par la cle d'appareil, sous
 * une clef par entreprise. Sans cache et sans reseau, on retombe sur SALES (D6) sans
 * marquer le mode comme connu, et la prochaine navigation retente la lecture.
 */
@Injectable({ providedIn: 'root' })
export class ModePosEntreprise {
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    private readonly _mode = signal<ModePos>('SALES');
    private readonly _connu = signal(false);

    readonly mode = this._mode.asReadonly();
    /** Vrai quand le mode vient d'une lecture reelle (serveur ou cache), pas du repli par defaut. */
    readonly connu = this._connu.asReadonly();
    readonly estVente = computed(() => this._mode() === 'SALES');
    readonly estRh = computed(() => this._mode() === 'HR');
    readonly estAucun = computed(() => this._mode() === 'NONE');

    private chargement: Promise<void> | null = null;
    private entrepriseChargee: string | null = null;

    /** Charge le mode une fois par entreprise ; les appels suivants rendent la meme promesse. */
    assurerCharge(): Promise<void> {
        const id = this.idEntreprise();
        if (id !== this.entrepriseChargee) {
            this.entrepriseChargee = id;
            this.chargement = null;
            this._connu.set(false);
            this._mode.set('SALES');
        }
        return (this.chargement ??= this.charger(id));
    }

    private idEntreprise(): string | null {
        return this.isBrowser ? localStorage.getItem('selectedCompanyId') : null;
    }

    private async charger(id: string | null): Promise<void> {
        // Pas d'entreprise choisie (ou rendu serveur) : rien a lire. `assurerCharge`
        // retentera quand l'identifiant changera.
        if (!this.isBrowser || !id) return;
        const enCache = await this.lireCache(id);
        if (enCache) {
            this.appliquer(enCache);
            // Le plan a pu changer depuis la derniere connexion : on rafraichit sans bloquer.
            void this.rafraichir(id);
            return;
        }
        await this.rafraichir(id);
    }

    private async rafraichir(id: string): Promise<void> {
        try {
            const reponse = await firstValueFrom(
                this.http
                    .get<ReponseModePos>(`${environment.apiUrls.partners}/plans/mode-pos/${id}`)
                    .pipe(timeout(DELAI_MS)),
            );
            const avant = this._connu() ? this._mode() : null;
            const mode = modeEffectif(reponse);
            this.appliquer(mode);
            await this.ecrireCache(id, mode);
            if (avant !== null && avant !== mode) {
                await this.router.navigate([mode === 'NONE' ? '/plan-sans-mobile' : '/home']);
            }
        } catch {
            // Reseau coupe ou serveur lent : le cache (ou le repli SALES) tient. On retentera.
            if (!this._connu() && this.entrepriseChargee === id) this.chargement = null;
        }
    }

    private appliquer(mode: ModePos): void {
        this._mode.set(mode);
        this._connu.set(true);
    }

    private async lireCache(id: string): Promise<ModePos | null> {
        try {
            const brut = localStorage.getItem(PREFIXE_MODE_POS + id);
            if (!brut) return null;
            const clair = await cleAppareil.dechiffrer(brut);
            if (clair === null) return null;
            const mode = (JSON.parse(clair) as { mode?: string }).mode;
            return (MODES as readonly (string | undefined)[]).includes(mode) ? (mode as ModePos) : null;
        } catch {
            return null;
        }
    }

    private async ecrireCache(id: string, mode: ModePos): Promise<void> {
        try {
            const chiffre = await cleAppareil.chiffrer(JSON.stringify({ mode }));
            if (chiffre !== null) localStorage.setItem(PREFIXE_MODE_POS + id, chiffre);
        } catch {
            // Pas de cache : le prochain demarrage hors ligne retombera sur le repli SALES.
        }
    }
}
